import React, { useEffect } from "react";
import { Helmet } from "react-helmet";

import Hero from "./components/Hero";
import About from "./components/About";
import Experience from "./components/Experience";
import Projects from "./components/Projects";
import Education from "./components/Education";
import Contact from "./components/Contact";
import Research from "./components/Research";
import Personal from "./components/Personal";

function App() {
  useEffect(() => {
    const items = document.querySelectorAll(".timeline-item, .card");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      { threshold: 0.1 }
    );

    items.forEach((item) => observer.observe(item));

    return () => {
      items.forEach((item) => observer.unobserve(item));
    };
  }, []);

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://harpreet-jk.onrender.com/#harpreet-singh",
    name: "Harpreet Singh",
    url: "https://harpreet-jk.onrender.com/",
    image: "https://harpreet-jk.onrender.com/harpreet.jpeg",

    jobTitle: "Project Manager & Software Developer",

    description:
      "Harpreet Singh is a Project Manager and Software Developer with 14+ years of experience in software development, project management, technology leadership, and software delivery.",

    worksFor: {
      "@type": "Organization",
      name: "Skyach Software Solutions Pvt. Ltd.",
    },

    knowsAbout: [
      "Project Management",
      "Software Development",
      "Team Leadership",
      "Ruby on Rails",
      "React",
      "JavaScript",
      "Databases",
      "Google Cloud Platform",
      "AWS",
      "PostgreSQL",
      "Redis",
    ],

    knowsLanguage: ["English", "Hindi", "Punjabi"],

    sameAs: [
      "https://www.linkedin.com/in/harpreet-singh-485a2928",
      "https://github.com/singhpreet86",
      "https://www.facebook.com/harpreet.singh.803260",
    ],
  };

  const profilePageSchema = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "@id": "https://harpreet-jk.onrender.com/#profile",
    url: "https://harpreet-jk.onrender.com/",
    name: "Harpreet Singh | Project Manager & Software Developer",
    description:
      "Professional profile of Harpreet Singh, a Project Manager and Software Developer with 14+ years of experience in software development, technology leadership, and project delivery.",
    mainEntity: {
      "@id": "https://harpreet-jk.onrender.com/#harpreet-singh",
    },
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://harpreet-jk.onrender.com/#website",
    url: "https://harpreet-jk.onrender.com/",
    name: "Harpreet Singh",
    description:
      "Official professional portfolio of Harpreet Singh, Project Manager and Software Developer.",
  };

  return (
    <div>
      <Helmet>
        {/* Primary SEO */}
        <title>Harpreet Singh | Project Manager & Software Developer</title>

        <meta
          name="description"
          content="Harpreet Singh is a Project Manager and Software Developer with 14+ years of experience in software development, project management, technology leadership, and software delivery."
        />

        <meta name="author" content="Harpreet Singh" />

        {/* Google Search Console */}
        <meta
          name="google-site-verification"
          content="Mcp_0fQkG4M78f1n9hrVqsSUY5gHopf5wn2C8iS5D9A"
        />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href="https://harpreet-jk.onrender.com/"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Harpreet Singh | Project Manager & Software Developer"
        />

        <meta
          property="og:description"
          content="Professional portfolio of Harpreet Singh, Project Manager and Software Developer with 14+ years of experience."
        />

        <meta
          property="og:url"
          content="https://harpreet-jk.onrender.com/"
        />

        <meta property="og:type" content="profile" />

        <meta
          property="og:image"
          content="https://harpreet-jk.onrender.com/harpreet.jpeg"
        />

        <meta
          property="og:site_name"
          content="Harpreet Singh"
        />

        {/* Twitter / X */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Harpreet Singh | Project Manager & Software Developer"
        />

        <meta
          name="twitter:description"
          content="Professional portfolio of Harpreet Singh, Project Manager and Software Developer."
        />

        <meta
          name="twitter:image"
          content="https://harpreet-jk.onrender.com/harpreet.jpeg"
        />

        {/* Person structured data */}
        <script type="application/ld+json">
          {JSON.stringify(personSchema)}
        </script>

        {/* ProfilePage structured data */}
        <script type="application/ld+json">
          {JSON.stringify(profilePageSchema)}
        </script>

        {/* WebSite structured data */}
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      </Helmet>

      <Hero />
      <About />
      <Experience />
      <Projects />
      <Education />
      <Research />
      <Personal />
      <Contact />
    </div>
  );
}

export default App;
